'use client';

import { useAnalytics } from '@repo/segment/Analytics';
import { useAnonymousId } from '@repo/segment/components/AnonymousIdProvider';
import { useClientOnlyConstant } from '@repo/segment/hooks/useClientOnlyConstant';
import { getDeviceIdWithoutHmac } from '@repo/segment/utils/getDeviceId';
import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { ABSmartlyProviderProps, type ABSmartlyContextType } from '../types';
import { hydrateClientContext } from '../utils/context';
import { useDeviceType } from '../utils/deviceType/hooks';
import { getABSmartlyEventLogger } from '../utils/eventLogging';
import type { SupportedExperimentVariants } from '../utils/types';
import { LogStaticExperiments } from './LogStaticExperiments';
import { abSmartlySdkConfig } from './config';

export const ABSmartlyContext = createContext<ABSmartlyContextType | null>(null);
export const useABSmartlyContext = () => useContext(ABSmartlyContext);

export const ABSmartlyProvider = ({
  contextData,
  children,
  staticExperimentResult,
}: PropsWithChildren<ABSmartlyProviderProps> & {
  staticExperimentResult: SupportedExperimentVariants;
}) => {
  const anonymousId = useAnonymousId();
  const { analytics, deployableVersion, gitSha } = useAnalytics();
  const [smartlyContext, setSmartlyContext] = useState<ABSmartlyContextType | null>(null);
  const deviceType = useDeviceType();
  const deviceId = useClientOnlyConstant(() => getDeviceIdWithoutHmac(), '');

  useEffect(() => {
    if (smartlyContext || !contextData || !analytics) {
      return;
    }

    const clientSideContext = hydrateClientContext({
      sdkConfig: {
        ...abSmartlySdkConfig,
        eventLogger: getABSmartlyEventLogger({
          analytics,
          anonymousId,
          deployableVersion,
          deviceId,
          gitSha,
          staticExperimentResult,
        }),
      },
      anonymousId,
      deviceType,
      contextData,
    });
    setSmartlyContext(clientSideContext);
  }, [analytics, anonymousId, deviceId, deviceType, contextData]);

  return (
    <ABSmartlyContext.Provider value={smartlyContext}>
      <LogStaticExperiments serverSideExperimentDecision={staticExperimentResult}>
        {children}
      </LogStaticExperiments>
    </ABSmartlyContext.Provider>
  );
};
